/**
 * MagliaEditor — editor della maglia squadra (template di Andrea, 2026-10-07).
 *
 * Controllato (value + onChange). Usato in Gestione Rosa e in registrazione.
 * Anteprima grande, 15 modelli, 5 colori (corpo, dettaglio, accento solo per
 * i modelli che lo usano, colletto, polsini). Le toppe scudetto e Champions
 * non si scelgono: compaiono da sole sulla maglia del vincitore in carica.
 */

import { useState } from "react";
import { Maglia, type MagliaPattern, modelloUsaAccento, SPONSOR_DEFAULT } from "./Maglia";
import { tr } from "../../i18n";

export interface MagliaState {
  pattern: MagliaPattern;
  coloreCorpo: string;
  coloreDettaglio: string;
  coloreAccento: string;
  coloreColletto: string;
  colorePolsini: string;
  /** null = automatico (bianco sulle maglie scure) */
  coloreSponsor: string | null;
  /** "logo_nome" (logo + scritta) o "logo" */
  sponsor: "logo_nome" | "logo";
  patchScudetto: boolean;
  patchChampions: boolean;
}

/** compatibilita': prima esistevano i diritti alle patch da scegliere a mano */
export interface DirittiPatchUI {
  scudetto: boolean;
  champions: boolean;
  supercoppa: boolean;
}

/** Default: i colori FantaSquad del template (viola, bianco, oro). */
export const MAGLIA_DEFAULT: MagliaState = {
  pattern: "tinta_unita",
  coloreCorpo: "#800080",
  coloreDettaglio: "#FFFFFF",
  coloreAccento: "#AA8800",
  coloreColletto: "#AA8800",
  colorePolsini: "#AA8800",
  coloreSponsor: null,
  sponsor: "logo_nome",
  patchScudetto: false,
  patchChampions: false,
};

const PATTERNS: { id: MagliaPattern; label: string }[] = [
  { id: "tinta_unita", label: tr("Tinta unita") },
  { id: "meta_verticale", label: tr("Metà verticale") },
  { id: "meta_verticale_inversa", label: tr("Metà inversa") },
  { id: "maniche_contrasto", label: tr("Maniche") },
  { id: "righe_verticali", label: tr("Righe verticali") },
  { id: "tre_righe_verticali", label: tr("Tre righe") },
  { id: "fascia_verticale", label: tr("Fascia verticale") },
  { id: "strisce_orizzontali", label: tr("Strisce orizz.") },
  { id: "fascia_orizzontale", label: tr("Fascia orizz.") },
  { id: "diagonale", label: tr("Diagonale") },
  { id: "diagonale_sx", label: tr("Diagonale sx") },
  { id: "croce_x", label: tr("Croce X") },
  { id: "crociata", label: tr("Crociata") },
  { id: "v", label: "V" },
  { id: "scacchi", label: tr("Scacchi") },
];

/** Colori pronti (Andrea 2026-10-07): quelli delle maglie di Serie A e
 *  della lega; per tutto il resto c'e' «Altro colore». */
export const COLORI_STANDARD: { hex: string; nome: string }[] = [
  { hex: "#FFFFFF", nome: tr("Bianco") },
  { hex: "#E5E7EB", nome: tr("Grigio chiaro") },
  { hex: "#808080", nome: tr("Grigio") },
  { hex: "#333333", nome: tr("Antracite") },
  { hex: "#000000", nome: tr("Nero") },
  { hex: "#FFCC00", nome: tr("Giallo") },
  { hex: "#AA8800", nome: tr("Oro") },
  { hex: "#DECD87", nome: tr("Beige") },
  { hex: "#FF7F00", nome: tr("Arancione") },
  { hex: "#FF0000", nome: tr("Rosso") },
  { hex: "#D40000", nome: tr("Rosso scuro") },
  { hex: "#AA0000", nome: tr("Rosso Genoa") },
  { hex: "#800000", nome: tr("Bordeaux") },
  { hex: "#7A1F3D", nome: tr("Granata") },
  { hex: "#FF66AA", nome: tr("Rosa") },
  { hex: "#800080", nome: tr("Viola") },
  { hex: "#2A0F4E", nome: tr("Viola notte") },
  { hex: "#AACCFF", nome: tr("Celeste") },
  { hex: "#5599FF", nome: tr("Azzurro") },
  { hex: "#0044AA", nome: tr("Blu") },
  { hex: "#0000FF", nome: tr("Blu elettrico") },
  { hex: "#000080", nome: tr("Blu navy") },
  { hex: "#022946", nome: tr("Blu notte") },
  { hex: "#00A19A", nome: tr("Turchese") },
  { hex: "#00B3B3", nome: tr("Ciano") },
  { hex: "#008000", nome: tr("Verde") },
  { hex: "#004D26", nome: tr("Verde scuro") },
  { hex: "#8BC34A", nome: tr("Verde lime") },
  { hex: "#6B4423", nome: tr("Marrone") },
];

type CampoColore =
  | "coloreCorpo"
  | "coloreDettaglio"
  | "coloreAccento"
  | "coloreColletto"
  | "colorePolsini"
  | "coloreSponsor";

const uguale = (a: string | null, b: string) => !!a && a.toLowerCase() === b.toLowerCase();

/** Una voce colore: pallino col colore attuale, si tocca per sceglierlo. */
function VoceColore({
  label,
  value,
  attiva,
  onClick,
}: {
  label: string;
  value: string | null;
  attiva: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-1 rounded-lg px-2 py-1.5 border transition text-xs ${
        attiva
          ? "border-accent bg-accent/10 text-fg"
          : "border-transparent text-fg-muted hover:text-fg"
      }`}
    >
      <span
        className="w-8 h-8 rounded-full border border-primary-soft flex items-center justify-center text-[9px] font-bold"
        style={value ? { background: value } : undefined}
      >
        {value ? "" : "AUTO"}
      </span>
      {label}
    </button>
  );
}

/** Griglia dei colori pronti + «Altro colore» (tavolozza del sistema). */
function SceltaColore({
  value,
  onChange,
  conAutomatico,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  conAutomatico?: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-8 sm:grid-cols-10 gap-1.5">
        {COLORI_STANDARD.map((c) => (
          <button
            key={c.hex}
            type="button"
            title={c.nome}
            aria-label={c.nome}
            onClick={() => onChange(c.hex)}
            className={`aspect-square rounded-full border transition ${
              uguale(value, c.hex)
                ? "border-accent ring-2 ring-accent scale-110"
                : "border-primary-soft hover:scale-110"
            }`}
            style={{ background: c.hex }}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {conAutomatico && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className={`px-2.5 py-1 rounded-full border ${
              value === null
                ? "border-accent bg-accent/15 text-accent"
                : "border-primary-soft text-fg-muted hover:text-fg"
            }`}
          >
            {tr("Automatico")}
          </button>
        )}
        <label className="flex items-center gap-2 px-2.5 py-1 rounded-full border border-primary-soft text-fg-muted hover:text-fg cursor-pointer">
          {tr("🎨 Altro colore")}
          <input
            type="color"
            value={value ?? SPONSOR_DEFAULT}
            onChange={(e) => onChange(e.target.value)}
            className="w-6 h-5 rounded cursor-pointer bg-transparent border-0 p-0"
          />
        </label>
        {value && !COLORI_STANDARD.some((c) => uguale(value, c.hex)) && (
          <span className="font-mono text-fg-muted">{value.toUpperCase()}</span>
        )}
      </div>
    </div>
  );
}

type Scheda = "modello" | "colori" | "sponsor";

export function MagliaEditor({
  value,
  onChange,
  logoDataUrl,
  nomeSquadra,
  ampio = false,
}: {
  value: MagliaState;
  onChange: (v: MagliaState) => void;
  /** non piu' usato: le toppe sono automatiche */
  diritti?: DirittiPatchUI;
  /** logo della squadra, per l'anteprima */
  logoDataUrl?: string | null;
  /** nome sotto l'anteprima */
  nomeSquadra?: string;
  /** impaginazione larga a due colonne (finestra di Gestione Rosa) */
  ampio?: boolean;
}) {
  const set = (patch: Partial<MagliaState>) => onChange({ ...value, ...patch });
  const [scheda, setScheda] = useState<Scheda>("modello");
  const [campo, setCampo] = useState<CampoColore>("coloreCorpo");
  const colori = {
    coloreCorpo: value.coloreCorpo,
    coloreDettaglio: value.coloreDettaglio,
    coloreAccento: value.coloreAccento,
    coloreColletto: value.coloreColletto,
    colorePolsini: value.colorePolsini,
    coloreSponsor: value.coloreSponsor,
    sponsor: value.sponsor,
  };
  const nomeModello = PATTERNS.find((p) => p.id === value.pattern)?.label ?? "";
  const vociColore = [
    ["coloreCorpo", tr("Corpo")],
    ["coloreDettaglio", tr("Dettaglio")],
    ...(modelloUsaAccento(value.pattern) ? [["coloreAccento", tr("Accento")]] : []),
    ["coloreColletto", tr("Colletto")],
    ["colorePolsini", tr("Polsini")],
  ] as [CampoColore, string][];
  const campoColori = vociColore.some(([k]) => k === campo) ? campo : "coloreCorpo";

  const vetrina = (
    <div className="relative rounded-2xl overflow-hidden border border-accent/25 bg-gradient-to-b from-primary-deep via-bg to-bg">
      {/* luce da vetrina */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.28), transparent 60%), radial-gradient(ellipse at 50% 100%, rgba(109,61,204,0.35), transparent 55%)",
        }}
      />
      <div className="relative flex flex-col items-center px-4 pt-5 pb-4">
        <div className="drop-shadow-[0_12px_18px_rgba(0,0,0,0.55)] transition-transform duration-300 hover:scale-[1.03]">
          <Maglia
            pattern={value.pattern}
            {...colori}
            patchScudetto={value.patchScudetto}
            patchChampions={value.patchChampions}
            logoDataUrl={logoDataUrl}
            size={ampio ? 230 : 170}
          />
        </div>
        {/* pavimento */}
        <div aria-hidden className="w-40 h-3 -mt-2 rounded-[50%] bg-black/40 blur-md" />
        {nomeSquadra && (
          <div className="mt-2 font-display text-lg text-accent tracking-wide text-center">
            {nomeSquadra}
          </div>
        )}
        <div className="text-[11px] uppercase tracking-[0.2em] text-fg-muted">{nomeModello}</div>
        <div className="mt-3 flex items-end gap-3 text-[10px] text-fg-muted">
          <span>{tr("Come la vedranno:")}</span>
          <Maglia pattern={value.pattern} {...colori} size={44} />
          <Maglia pattern={value.pattern} {...colori} size={28} />
          <Maglia pattern={value.pattern} {...colori} size={20} />
        </div>
      </div>
    </div>
  );

  const linguette = (
    <div className="flex gap-1 p-1 rounded-xl bg-primary-deep/70 border border-primary/30">
      {(
        [
          ["modello", tr("👕 Modello")],
          ["colori", tr("🎨 Colori")],
          ["sponsor", tr("⭐ Sponsor")],
        ] as [Scheda, string][]
      ).map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => setScheda(k)}
          className={`flex-1 px-2 py-2 rounded-lg text-xs font-bold transition ${
            scheda === k ? "bg-accent text-ink shadow" : "text-fg-muted hover:text-fg"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const contenuto = (
    <div className="space-y-3">
      {scheda === "modello" && (
        <div className={`grid gap-2 ${ampio ? "grid-cols-5" : "grid-cols-4 sm:grid-cols-5"}`}>
          {PATTERNS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => set({ pattern: p.id })}
              title={p.label}
              className={`group rounded-xl p-1.5 border transition flex flex-col items-center gap-1 ${
                value.pattern === p.id
                  ? "border-accent bg-accent/15 shadow-[0_0_0_1px_rgba(212,175,55,0.5)]"
                  : "border-primary/30 bg-primary-deep/40 hover:border-accent/50 hover:bg-primary-deep/70"
              }`}
            >
              <Maglia pattern={p.id} {...colori} size={52} />
              <span
                className={`text-[9px] leading-tight text-center ${
                  value.pattern === p.id
                    ? "text-accent font-bold"
                    : "text-fg-muted group-hover:text-fg"
                }`}
              >
                {p.label}
              </span>
            </button>
          ))}
        </div>
      )}

      {scheda === "colori" && (
        <div className="space-y-3">
          <div className="flex flex-wrap justify-center gap-1">
            {vociColore.map(([k, label]) => (
              <VoceColore
                key={k}
                label={label}
                value={value[k]}
                attiva={campoColori === k}
                onClick={() => setCampo(k)}
              />
            ))}
          </div>
          <div className="rounded-xl border border-primary/30 bg-primary-deep/40 p-3">
            <SceltaColore
              value={value[campoColori]}
              onChange={(v) =>
                set({ [campoColori]: v ?? value[campoColori] } as Partial<MagliaState>)
              }
            />
          </div>
        </div>
      )}

      {scheda === "sponsor" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {(
              [
                ["logo_nome", tr("Logo e scritta")],
                ["logo", tr("Solo logo")],
              ] as ["logo_nome" | "logo", string][]
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => set({ sponsor: k })}
                className={`rounded-xl p-2 border transition flex flex-col items-center gap-1 ${
                  value.sponsor === k
                    ? "border-accent bg-accent/15"
                    : "border-primary/30 bg-primary-deep/40 hover:border-accent/50"
                }`}
              >
                <Maglia pattern={value.pattern} {...colori} sponsor={k} size={72} />
                <span
                  className={`text-xs ${value.sponsor === k ? "text-accent font-bold" : "text-fg-muted"}`}
                >
                  {label}
                </span>
              </button>
            ))}
          </div>
          <div className="rounded-xl border border-primary/30 bg-primary-deep/40 p-3 space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-fg-muted">
              {tr("Colore dello sponsor")}
            </div>
            <SceltaColore
              value={value.coloreSponsor}
              conAutomatico
              onChange={(v) => set({ coloreSponsor: v })}
            />
            <div className="text-[10px] text-fg-muted">
              {tr("Automatico: bianco sulle maglie scure, viola su quelle chiare.")}
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-fg-muted text-center">
        {tr(
          tr(
            "🏆 Scudetto e toppa della coppa compaiono da soli sulla maglia di chi ha vinto l'ultima stagione. Il logo della squadra va in alto a destra.",
          ),
        )}
      </p>
    </div>
  );

  if (!ampio) {
    return (
      <div className="space-y-4">
        {vetrina}
        {linguette}
        {contenuto}
      </div>
    );
  }
  return (
    <div className="grid gap-5 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <div className="md:sticky md:top-0 self-start">{vetrina}</div>
      <div className="space-y-4">
        {linguette}
        {contenuto}
      </div>
    </div>
  );
}

/** Stato dell'editor dai dati della squadra. */
export function magliaDaSquadra(s: {
  maglia_pattern: string;
  maglia_colore_corpo: string;
  maglia_colore_dettaglio: string;
  maglia_colore_maniche: string;
  maglia_colore_accento?: string | null;
  maglia_colore_colletto?: string | null;
  maglia_colore_polsini?: string | null;
  maglia_colore_sponsor?: string | null;
  maglia_sponsor?: string | null;
  maglia_patch_scudetto?: boolean;
  maglia_patch_champions?: boolean;
}): MagliaState {
  return {
    pattern: s.maglia_pattern as MagliaPattern,
    coloreCorpo: s.maglia_colore_corpo,
    coloreDettaglio: s.maglia_colore_dettaglio,
    coloreAccento: s.maglia_colore_accento || s.maglia_colore_dettaglio,
    coloreColletto: s.maglia_colore_colletto || s.maglia_colore_maniche,
    colorePolsini: s.maglia_colore_polsini || s.maglia_colore_maniche,
    coloreSponsor: s.maglia_colore_sponsor || null,
    sponsor: s.maglia_sponsor === "logo" ? "logo" : "logo_nome",
    patchScudetto: !!s.maglia_patch_scudetto,
    patchChampions: !!s.maglia_patch_champions,
  };
}

/** Campi da mandare al server (il vecchio "maniche" = polsini). */
export function magliaPayload(m: MagliaState) {
  return {
    maglia_pattern: m.pattern,
    maglia_colore_corpo: m.coloreCorpo,
    maglia_colore_dettaglio: m.coloreDettaglio,
    maglia_colore_maniche: m.colorePolsini,
    maglia_colore_accento: m.coloreAccento,
    maglia_colore_colletto: m.coloreColletto,
    maglia_colore_polsini: m.colorePolsini,
    maglia_colore_sponsor: m.coloreSponsor,
    maglia_sponsor: m.sponsor,
  };
}
